// ---------------------------------------------------------------------------
// 붙여넣은 원본 마크업을 렌더 가능한 형태로 정제하고, 그것을 담을 프레임
// 문서를 만든다.
//
// 이 화면은 사용자가 어디선가 복사해 온 마크업이다. iframe 자체가
// `allow-scripts` 없이 샌드박스되지만(as-is-view.tsx), 방어는 겹쳐 둔다 —
// 스크립트·인라인 핸들러·중첩 프레임(iframe/frame/frameset/object/embed/applet)은
// 삽입 전에 제거한다.
//
// 정제 실패(빈 결과)와 상한 초과는 모두 null이다: 빈 프레임이나 잘린 화면을
// "원본"이라 부르는 것이 이 기능에서 가장 나쁜 결과다.
//
// 순수 함수, 앱 상태에 대한 의존성 없음 — 캡처 레인(features/chat/services)과
// 뷰(features/preview/components) 양쪽에서 임포트되므로 이 파일에는 그 외
// 어떤 것도 import하지 않는다. 바이트 측정에 `Buffer`가 아니라
// `TextEncoder`를 쓰는 것도 같은 이유: 이 함수는 브라우저 번들에서 실행되고
// `Buffer`는 브라우저 전역이 아니다(Node 전용). `.length`는 쓰지 않는다 —
// UTF-16 코드 유닛을 세므로 한글 마크업에서 바이트 상한을 실제보다 낮게
// 잘못 잰다.
//
// 이 파일은 `<% %>` 스크립틀릿을 독립적으로 제거한다. 캡처 레인의
// legacy-screen-readback.ts도 파싱 전에 동일하게 제거하지만, 두 경로는
// 서로 다른 입력에서 각자 실행되며 어느 쪽도 상대가 실행됐다고 가정하지
// 않는다.
//
// 두 가지 산출물이 서로 다른 약속을 진다:
//   • `sanitizeAsIsMarkup` — 서버에 **저장되는** 바이트(`sourceMarkup.markup`).
//     "이 마크업은 정제됐다"는 주장은 이 저장물이 지켜야 한다.
//   • `buildAsIsFrameDocument` — 그 바이트를 **화면에 띄우는** 문서. 저장물은
//     건드리지 않고, 프레임 안에서만 항해(navigation)를 불가능하게 만든다.
// ---------------------------------------------------------------------------

export const AS_IS_MARKUP_MAX_BYTES = 262144

const SCRIPTLET = /<%[\s\S]*?%>/g
// Closed `<script>...</script>`, OR — mirroring FRAME_TAG's fallback branch
// below — a bare opening `<script ...>` with no matching `</script>`
// anywhere after it. A paste that got cut off mid-tag (or was deliberately
// left unclosed) must not slip through untouched: the first alternative
// requires an eventual closing tag to match at all, so an unclosed tag falls
// through to the second alternative, which strips just the opening tag and
// leaves whatever followed as inert plain text (not re-wrapped in a script
// element, so it never executes).
const SCRIPT_TAG = /<script\b[\s\S]*?<\/script\s*>|<script\b[^>]*\/?>/gi
// FINAL-REVIEW M1 — `<frame>`, `<frameset>` and `<applet>` were missing while
// the module header claimed nested frames are removed. `frameset` is listed
// before `frame` for readability; `\b` is what actually keeps `<frameset>`
// from being matched as `<frame>` + `set`.
const FRAME_TAG =
  /<(iframe|frameset|frame|object|embed|applet)\b[\s\S]*?<\/\1\s*>|<(iframe|frameset|frame|object|embed|applet)\b[^>]*\/?>/gi
// ---------------------------------------------------------------------------
// ATTRIBUTE SCANNER (round 5) — replaces the two regexes that used to guess at
// attribute boundaries (`INLINE_HANDLER`, `JS_URL_ATTR`).
//
// Why a scanner and not a wider pattern. Both regexes required a SEPARATOR
// before the attribute name. The tokenizer requires no such thing: its
// *after-attribute-value-(quoted)* state treats any character that is not
// whitespace, `/` or `>` as a parse error and RECONSUMES it in
// before-attribute-name. So `<img src="x"onerror=alert(1)>` is a live handler
// and `<a href="x"target="_self">` is a live target, with no separator byte
// anywhere. Round 3 answered this class by widening `\s` to `[\s/]`; widening
// it once more to `[\s/"']` would answer today's three inputs and leave the
// next spelling open — and the widening had already begun eating bytes out of
// ordinary URLs (`href="/portal/online=1"` matched `/online=`, and the
// bare-value branch then swallowed the closing quote and destroyed the tag,
// storing the corruption as "the original"). Hand-widening a separator class
// twice is the signal that the separator class is the wrong mechanism: the
// tokenizer has no separators, it has STATES.
//
// This walks tags the way the tokenizer does — tag-name, before-attribute-name
// (skipping whitespace and `/`), attribute-name, before/after-attribute-value,
// the three attribute-value states, and the reconsume-in-before-attribute-name
// exit from a quoted value — and reports each attribute's SPAN. Callers then
// splice the original bytes by offset, so everything they do not name is
// preserved exactly. It also means text that merely looks like an attribute
// (`<div>예: onclick="doIt()"…</div>`) is left alone, because the scanner
// knows it is not in a tag.
//
// Deliberately NOT a full parser: element removal (scriptlets, script/frame
// elements) stays byte-level regex + the fixpoint below, and raw-text element
// contents (`<style>`) are walked like ordinary markup. This is the attribute
// surface only, which is where every separator finding lived.
// ---------------------------------------------------------------------------

/** HTML whitespace — tab, LF, FF, CR, space. NOT JavaScript's `\s`. */
const HTML_WS = /[\t\n\f\r ]/
const TAG_NAME_END = /[\t\n\f\r />]/
const ATTR_NAME_END = /[\t\n\f\r />=]/
const UNQUOTED_VALUE_END = /[\t\n\f\r >]/
const ASCII_ALPHA = /[a-zA-Z]/

/**
 * Where does a comment END? Given `markup.slice(start, start + 4) === '<!--'`,
 * returns the offset just past its closing `>`, or **-1** when the comment
 * genuinely runs to EOF (eof-in-comment).
 *
 * Exported because it is the ONE answer to that question. The As-Is capture
 * lane's trim anchor (`legacy-screen-as-is-capture.ts`) used to carry a second
 * one — a regex that knew only `-->` — so a paste opening with `<!-->` had its
 * anchor land on the next tag and the visible text in between was silently
 * deleted before sanitization. Two implementations of "where does a comment
 * end" is how they drift; there is now one.
 *
 * ROUND 6 — this used to be `markup.indexOf('-->', start + 4)`, which knows
 * exactly one of the tokenizer's four ways to close a comment. It also closes
 * on `>` in **comment-start** (`<!-->`), on `>` in **comment-start-dash**
 * (`<!--->`), and on `--!>` in **comment-end-bang**. On any of those the naive
 * search ran off the end and the scanner reported NO ATTRIBUTES FOR THE REST
 * OF THE DOCUMENT — so prefixing a paste with `<!-->` disarmed
 * `neutralizeNavigationTargets` and reopened the navigation Critical. (Inline
 * handlers are inert under `sandbox=""`; `target` is not, which is the whole
 * reason that function exists.)
 *
 * SCOPE, stated precisely rather than as "walks like the tokenizer": this
 * computes where a comment ENDS. It is not a comment parser — it does not
 * collect comment data, and it deliberately omits the comment-less-than-sign
 * chain (`lt` / `lt-bang` / `lt-bang-dash` / `lt-bang-dash-dash`), which in the
 * spec exists to record nested-comment parse errors and to append the right
 * text. Those four states are TERMINATION-EQUIVALENT to the plain dash path:
 * an inner `<!--` reaches comment-end through its own two dashes either way.
 * Measured, not assumed — a differential over 297,656 inputs (exhaustive to
 * length 7 over `< ! - > a`, plus 200,000 random) found 0 divergences, and a
 * mutation deleting the chain failed 0 tests, which is exactly why it is not
 * here: code no test can defend does not belong in a sanitizer. The
 * downlevel-revealed idiom `<!--[if gt IE 7]><!-->` still ends at that `>`,
 * and there is a test on it.
 *
 * Every `reconsume` transition below changes state without advancing, and no
 * cycle of them exists — each reaches a state that advances or returns within
 * two steps — so the loop always terminates.
 */
type CommentState =
  | 'start'
  | 'start-dash'
  | 'comment'
  | 'end-dash'
  | 'end'
  | 'end-bang'

export function findCommentEnd(markup: string, start: number): number {
  const len = markup.length
  let i = start + 4
  let state: CommentState = 'start'

  while (i < len) {
    const c = markup[i]
    switch (state) {
      case 'start':
        if (c === '-') {
          state = 'start-dash'
          i += 1
        } else if (c === '>') {
          return i + 1 // abrupt-closing-of-empty-comment: `<!-->`
        } else {
          state = 'comment' // reconsume
        }
        break
      case 'start-dash':
        if (c === '-') {
          state = 'end'
          i += 1
        } else if (c === '>') {
          return i + 1 // abrupt-closing-of-empty-comment: `<!--->`
        } else {
          state = 'comment' // reconsume
        }
        break
      case 'comment':
        if (c === '-') state = 'end-dash'
        i += 1
        break
      case 'end-dash':
        if (c === '-') {
          state = 'end'
          i += 1
        } else {
          state = 'comment' // reconsume
        }
        break
      case 'end':
        if (c === '>') return i + 1 // `-->`
        if (c === '!') {
          state = 'end-bang'
          i += 1
        } else if (c === '-') {
          i += 1 // stay: `--->` closes too
        } else {
          state = 'comment' // reconsume
        }
        break
      case 'end-bang':
        if (c === '-') {
          state = 'end-dash'
          i += 1
        } else if (c === '>') {
          return i + 1 // incorrectly-closed-comment: `--!>`
        } else {
          state = 'comment' // reconsume
        }
        break
    }
  }

  return -1 // eof-in-comment
}

/**
 * Elements whose CONTENT the tokenizer reads as raw text or RCDATA: anything
 * tag-shaped inside them is TEXT, not markup.
 *
 * ROUND 7 — the scanner read that content as markup, which broke in both
 * directions from one cause:
 *
 *   • OVER-REMOVAL, against this module's own promise: a handler written as
 *     demo content inside `<textarea>` was deleted from the STORED artifact —
 *     changing the original we present as the original, to remove something
 *     that could never execute.
 *   • UNDER-REMOVAL, against safety: `<!--` inside `<style>` is text, but the
 *     scanner took it for a comment open, ran off the end of the document, and
 *     then reported no attributes for everything after it — including a live
 *     `target`, which is navigable.
 *
 * `script` and `iframe` are listed for completeness; both element bodies are
 * already removed wholesale by SCRIPT_TAG/FRAME_TAG before this pass runs.
 *
 * These names only mean raw text in the HTML namespace. Inside `<svg>` or
 * `<math>` they are ordinary FOREIGN elements — `svg:title`, `svg:style` and
 * `svg:textarea` have no raw-text behaviour, and `</svg>` pops the whole
 * foreign subtree whether or not they were closed. Round 8 added
 * `FOREIGN_ROOTS` and the depth counter in the scanner for exactly that; see
 * the comment there for why the resulting divergences are all safe-side.
 *
 * `noscript` is deliberately ABSENT. Its content is raw text only when the
 * scripting flag is ENABLED. The As-Is frame is `sandbox=""` with no
 * `allow-scripts`, so scripting is disabled and `<noscript>` children are real
 * elements there — treating it as raw text would reopen the under-removal
 * direction above. There is a test pinning exactly that.
 */
const RAW_TEXT_ELEMENTS = new Set([
  'script',
  'style',
  'textarea',
  'title',
  'xmp',
  'iframe',
  'noembed',
  'noframes',
])

/**
 * Elements that switch the tree builder into FOREIGN content. Everything
 * between them and their end tag is SVG/MathML, where the raw-text names above
 * carry no raw-text behaviour.
 */
const FOREIGN_ROOTS = new Set(['svg', 'math'])

/**
 * The offset of the appropriate end tag closing a raw-text/RCDATA element, or
 * `markup.length` when there is none (the content then runs to EOF, which is
 * what the tokenizer does). Returns the position of the `<` so the end tag
 * itself is walked normally — it is an end tag, so it reports nothing.
 *
 * "Appropriate" is the spec's: `</` + the element's own name + a character
 * that can terminate a tag name. `</styleX>` does not close a `<style>`, and
 * `</style` at EOF is text, not an end tag.
 */
function findRawTextEnd(markup: string, from: number, tagName: string): number {
  const len = markup.length
  for (let i = from; i < len; i += 1) {
    if (markup[i] !== '<' || markup[i + 1] !== '/') continue
    const nameStart = i + 2
    const nameEnd = nameStart + tagName.length
    if (markup.slice(nameStart, nameEnd).toLowerCase() !== tagName) continue
    const after = markup[nameEnd]
    if (after !== undefined && TAG_NAME_END.test(after)) return i
  }
  return len
}

interface ScannedAttribute {
  /** Offset of the attribute name's first byte. */
  start: number
  /** Offset just past the attribute name. */
  nameEnd: number
  /** Offset just past the whole attribute (past its value, or past the name). */
  end: number
  /** Lowercased attribute name. */
  name: string
  /** Raw source text of the value, quotes excluded; '' when valueless. */
  value: string
}

function scanTagAttributes(markup: string): ScannedAttribute[] {
  const found: ScannedAttribute[] = []
  const len = markup.length
  let i = 0
  // The open <svg>/<math> roots, innermost last. A STACK, not a counter: the
  // tree builder ignores a foreign end tag whose name is nowhere on the stack
  // (in-foreign-content, "any other end tag"), so a stray `</math>` must not
  // close an open `<svg>`. A counter got that wrong, and my own differential
  // fuzz caught it leaking a live handler: `<svg foo=bar/></math><noembed>`
  // then took the raw-text skip and never scanned what followed.
  const foreignRoots: string[] = []

  while (i < len) {
    if (markup[i] !== '<') {
      i += 1
      continue
    }
    if (markup.startsWith('<!--', i)) {
      const commentEnd = findCommentEnd(markup, i)
      i = commentEnd === -1 ? len : commentEnd
      continue
    }
    // DOCTYPE / bogus comment (`<!…`, `<?…`) — both end at the first `>`.
    if (markup.startsWith('<!', i) || markup.startsWith('<?', i)) {
      const close = markup.indexOf('>', i + 2)
      i = close === -1 ? len : close + 1
      continue
    }
    // End tags may carry attributes, but the tree builder discards them, so
    // nothing in them is ever live. Walk them to find the tag's end; report
    // nothing, which keeps their bytes untouched.
    const isEndTag = markup.startsWith('</', i)
    const tagNameStart = i + (isEndTag ? 2 : 1)
    const tagNameFirst = markup[tagNameStart]
    if (isEndTag && tagNameFirst !== undefined && !ASCII_ALPHA.test(tagNameFirst)) {
      // `</>` is discarded; `</` + anything else opens a bogus comment that
      // runs to the first `>`. Either way nothing inside is a live tag, so
      // skip the region rather than reading a `<` inside it as one.
      if (tagNameFirst === '>') {
        i = tagNameStart + 1
      } else {
        const close = markup.indexOf('>', tagNameStart)
        i = close === -1 ? len : close + 1
      }
      continue
    }
    if (tagNameFirst === undefined || !ASCII_ALPHA.test(tagNameFirst)) {
      i += 1
      continue
    }

    let j = tagNameStart
    while (j < len && !TAG_NAME_END.test(markup[j])) j += 1
    const tagName = markup.slice(tagNameStart, j).toLowerCase()

    // In FOREIGN content the self-closing flag is honoured, so `<svg/>` opens
    // and closes in one token. The flag is only set by a `/` consumed in
    // before-attribute-name — a trailing `/` inside an unquoted attribute
    // value (`<svg foo=bar/>`) is part of the value, not a self-closing slash.
    let selfClosing = false
    while (j < len) {
      // before-attribute-name
      let sawSlash = false
      while (j < len && (HTML_WS.test(markup[j]) || markup[j] === '/')) {
        sawSlash = markup[j] === '/'
        j += 1
      }
      if (j >= len) break
      if (markup[j] === '>') {
        selfClosing = sawSlash
        j += 1
        break
      }

      const start = j
      while (j < len && !ATTR_NAME_END.test(markup[j])) j += 1
      if (j === start) {
        // A name that starts on `=` (a parse error the tokenizer folds into
        // the name). Step over it so the loop cannot stall.
        j += 1
        continue
      }
      const nameEnd = j
      const name = markup.slice(start, nameEnd).toLowerCase()

      // after-attribute-name
      let k = j
      while (k < len && HTML_WS.test(markup[k])) k += 1

      let end = nameEnd
      let value = ''
      if (markup[k] === '=') {
        k += 1
        while (k < len && HTML_WS.test(markup[k])) k += 1
        const quote = markup[k]
        if (quote === '"' || quote === "'") {
          const valueStart = k + 1
          const closing = markup.indexOf(quote, valueStart)
          value = markup.slice(valueStart, closing === -1 ? len : closing)
          end = closing === -1 ? len : closing + 1
        } else {
          const valueStart = k
          let m = k
          while (m < len && !UNQUOTED_VALUE_END.test(markup[m])) m += 1
          value = markup.slice(valueStart, m)
          end = m
        }
        j = end
      } else {
        j = nameEnd
      }

      if (!isEndTag) found.push({ start, nameEnd, end, name, value })
    }

    i = j > i ? j : i + 1

    // The tag is closed. Three cases, in this order:
    //
    // ROUND 8 — the raw-text skip is a TREE-BUILDER state, not a tag name.
    // Inside <svg>/<math> the raw-text names are foreign elements with no
    // raw-text behaviour, and </svg> pops the subtree whether or not they were
    // closed. Keying the skip on the name alone sent `findRawTextEnd` hunting
    // an `</title>` that never comes, which returned `markup.length` and left
    // every attribute in the rest of the document unscanned — including a live
    // `target`, which navigates. Note the shape of that bug: the EOF guard in
    // `findRawTextEnd` was correct (it returns len only when no end tag
    // exists); the branch was ENTERED when it should not have been. Namespace
    // question, not terminator question.
    //
    // The depth counter is deliberately cruder than the tree builder, and
    // every way it is cruder errs the same direction — it keeps us OUT of the
    // raw-text skip, which is the pre-round-7 behaviour of scanning everything:
    //   • HTML integration points (svg `foreignObject`/`desc`/`title`, MathML
    //     `annotation-xml`/`mtext`) re-enter HTML rules, so an HTML <style>
    //     nested in one really is raw text and we will scan it anyway;
    //   • the HTML breakout tags (<p>, <br>, <table>, …) pop foreign content
    //     early and we keep counting;
    //   • an end tag whose name is nowhere on the stack is ignored, exactly as
    //     the tree builder ignores it (this is why it is a stack: a counter
    //     decremented on a stray `</math>` and left foreign content early).
    // Scanning where the tokenizer would not can only remove MORE (an
    // over-removal against byte preservation, bounded to these shapes); it can
    // never leave a live handler or target behind.
    if (isEndTag) {
      if (FOREIGN_ROOTS.has(tagName)) {
        const open = foreignRoots.lastIndexOf(tagName)
        if (open !== -1) foreignRoots.length = open
      }
    } else if (FOREIGN_ROOTS.has(tagName)) {
      if (!selfClosing) foreignRoots.push(tagName)
    } else if (
      foreignRoots.length === 0 &&
      i < len &&
      RAW_TEXT_ELEMENTS.has(tagName)
    ) {
      // (A self-closing slash does not change this for HTML elements —
      // `<style/>` still enters raw text.)
      i = findRawTextEnd(markup, i, tagName)
    }
  }

  return found
}

/** Splice out the named spans, keeping every byte outside them. */
function removeSpans(
  markup: string,
  spans: readonly { start: number; end: number }[],
): string {
  let out = ''
  let cursor = 0
  for (const span of spans) {
    out += markup.slice(cursor, span.start)
    cursor = span.end
  }
  return out + markup.slice(cursor)
}

/** `onclick`, `onerror`, … — an inline event handler, whatever its value. */
const EVENT_HANDLER_NAME = /^on[a-z]+$/
/** `href`/`src`/`action`, with or without a namespace prefix (`xlink:href`). */
const URL_ATTR_NAME = /^(?:[a-z0-9_.-]+:)?(?:href|src|action)$/

// Normalizes a URL-attribute value just enough to catch the scheme-smuggling
// forms browsers themselves reconstitute when parsing a URL. The governing
// rule (not a growing blocklist): any entity — numeric OR named — that
// decodes to a character this function already strips or folds must be
// decoded BEFORE that strip/fold runs, or the decode is pointless. That is
// why every decode step below runs first and the whitespace strip runs
// LAST: a numeric or named entity for tab/newline/CR only becomes harmless
// once it has actually become a real tab/newline/CR character for the strip
// step to catch. (Round 1 got this backwards — strip ran before decode —
// which is exactly why `&#9;`/`&#10;` silently survived until round 3
// caught it as a numeric instance of the same class as `&Tab;`/`&NewLine;`.)
//
// This is deliberately NOT a general HTML entity decoder. Only entities that
// decode into a character already handled below are folded, and each one is
// verified against a spec-conformant parser (jsdom) before being added —
// see the report for what was checked and rejected. Today that set is:
//   - numeric character references (`&#58;`, `&#x3a;`) — any character,
//     decoded generically;
//   - `&colon;` — the one named entity decoding to `:` (see the comment
//     at its replace call for the case-sensitivity/no-legacy-form reasoning);
//   - `&Tab;` / `&NewLine;` — the two named entities decoding to characters
//     (`\t`, `\n`) this function already strips as literals (round 3).
// If a new bypass surfaces via some other named entity, extend this
// function explicitly (verify the decode first) — do not reach for a
// general-purpose decoder here.
function normalizeForSchemeCheck(value: string): string {
  return (
    value
      // 1. Numeric character references decode first, generically, for any
      // character — including tab/newline/CR, which step 3 below strips.
      .replace(/&#x([0-9a-f]+);?/gi, (_match, hex: string) =>
        String.fromCharCode(parseInt(hex, 16)),
      )
      .replace(/&#([0-9]+);?/g, (_match, dec: string) =>
        String.fromCharCode(parseInt(dec, 10)),
      )
      // 2. Named entities — folded BEFORE lowercasing and matched
      // case-sensitively on purpose, exactly as argued for `&colon;` in
      // round 2: each of these is a DIFFERENT named reference from its
      // other-case spelling, decoding to a different (or no) character.
      //
      // `&colon;` (lowercase 'c', semicolon required) is the only named
      // entity in the whole WHATWG table decoding to a character in the
      // literal string "javascript:" — verified against the table; none of
      // j/a/v/s/c/r/i/p/t has a named-entity equivalent. `&Colon;` (capital
      // 'C') decodes to U+2237 PROPORTION (∷), an unrelated math symbol —
      // folding it here would corrupt real, non-attack content. A
      // semicolon-less `&colon` does not decode either (not on the HTML5
      // legacy without-semicolon allowlist), so it is not matched.
      .replace(/&colon;/g, ':')
      // `&Tab;` (capital 'T') and `&NewLine;` (capital 'N', 'L') are the
      // named entities decoding to the tab/newline this function already
      // strips — verified live in jsdom: `&Tab;`/`&NewLine;` resolve
      // `el.href` to a live `javascript:` URL, while `&tab;`, `&TAB;`,
      // `&newline;`, `&NEWLINE;`, and semicolon-less forms of either do NOT
      // decode (checked, not assumed) and are correctly left unmatched.
      // There is no named entity for CR alone (checked `&CR;`, `&cr;`,
      // `&CarriageReturn;` — none decode); CR is only reachable numerically
      // (`&#13;`, `&#xd;`), already covered by step 1.
      .replace(/&Tab;/g, '\t')
      .replace(/&NewLine;/g, '\n')
      // 3. THE URL SPEC'S OWN NORMALIZATION, verbatim — runs LAST, after
      // every decode step above (see the governing rule at the top of this
      // comment). Two distinct operations, in the URL parser's own terms:
      //
      //   (a) "remove all ASCII tab or newline" — anywhere in the URL;
      //   (b) "strip leading and trailing C0 control or space" — U+0000
      //       through U+0020.
      //
      // FINAL-REVIEW I2: (b) used to be JavaScript's `.trim()`, and the two
      // are NOT the same set in either direction. `.trim()` misses
      // U+0001–U+0008 and U+000E–U+001F, so `href="\u0001javascript:…"` was
      // a live javascript: URL to the browser and an ordinary relative path
      // to this function — no entity needed. In the other direction
      // `.trim()` strips U+00A0 and other Unicode spaces, which the URL
      // parser does NOT, so an href starting with U+00A0 is a harmless
      // relative URL that we were deleting from the user's own bytes.
      // Matching the spec's trim closes the bypass AND stops the
      // over-removal — which is why the round-3 habit of extending a
      // character list was the wrong move here.
      .replace(/[\t\n\r]/g, '')
      .replace(/^[\u0000-\u0020]+/, '')
      .replace(/[\u0000-\u0020]+$/, '')
      .toLowerCase()
  )
}

/**
 * Remove every inline event handler and every `javascript:` URL attribute,
 * by span.
 *
 * Only the attribute's OWN span goes — never the separator in front of it.
 * That is what stops an attribute deletion from splicing (`<scr onclick="x"ipt>`
 * becomes `<scr ipt>`, not `<script>`): nothing that was separated becomes
 * adjacent, so the construct the old regex used to manufacture is never
 * created in the first place. It also deletes strictly fewer of the user's
 * bytes, which is the promise this module is under.
 */
function stripUnsafeAttributes(markup: string): {
  markup: string
  removed: number
} {
  const doomed = scanTagAttributes(markup).filter(
    (attr) =>
      EVENT_HANDLER_NAME.test(attr.name) ||
      (URL_ATTR_NAME.test(attr.name) &&
        normalizeForSchemeCheck(attr.value).startsWith('javascript:')),
  )
  if (doomed.length === 0) return { markup, removed: 0 }
  return { markup: removeSpans(markup, doomed), removed: doomed.length }
}

/**
 * 한 바퀴. 모든 패스는 **삭제만** 한다(치환 문자열은 항상 빈 문자열) — 이것이
 * 아래 고정점 루프의 종료 근거다.
 */
function sanitizePass(markup: string): { markup: string; removed: number } {
  let removed = 0
  let next = markup
  for (const re of [SCRIPTLET, SCRIPT_TAG, FRAME_TAG]) {
    removed += (next.match(re) ?? []).length
    next = next.replace(re, '')
  }
  const attrs = stripUnsafeAttributes(next)
  return { markup: attrs.markup, removed: removed + attrs.removed }
}

/**
 * FINAL-REVIEW I1 — 한 바퀴로는 닫히지 않는다.
 *
 * 각 정규식이 고정된 순서로 정확히 한 번씩만 돌면, 뒤 패스의 **삭제가 이어
 * 붙인** 것은 아무도 다시 보지 않는다. 즉 삭제가 제거 대상을 **만들어낸다**:
 * `<scr onclick="x"ipt>alert(document.cookie)</script>`에서 인라인 핸들러를
 * 지우면 살아 있는 `<script>`가 조립된다. 2차 결함이 더 나쁘다 — 그 출력에
 * 붙는 `removed: 1`이 화면에서는 "안전을 위해 제거된 요소 1건"으로 읽힌다.
 * 잘못 센 것이 아니라 거짓 안심이다.
 *
 * 그래서 패스 목록을 **변화가 멈출 때까지** 돌린다(고정점).
 *
 * 라운드 5 보강: 속성 삭제는 더 이상 구분자를 지우지 않으므로 **속성 쪽에서는
 * 접합이 생기지 않는다**. 남은 접합원은 요소 단위 삭제(SCRIPTLET/SCRIPT_TAG/
 * FRAME_TAG)뿐이고, 아래 루프가 그것을 닫는다.
 *
 * 종료 근거: 모든 패스는 삭제만 하고(치환값은 빈 문자열), 정규식은 빈
 * 문자열에 매치될 수 없으므로 무언가를 지운 바퀴는 길이를 최소 1 줄인다.
 * 따라서 고정점은 늦어도 `markup.length` 바퀴 안에 도달한다.
 *
 * 상한의 **실제 경계**(테스트로 실측): 루프는 최대 `MAX_SANITIZE_ROUNDS`번
 * 도는데 그중 한 번은 "더 이상 변하지 않는다"를 확인하는 데 쓰인다. 그래서
 * 견디는 접합 깊이는 11이고, 12부터 소진된다. 실제 붙여넣기는 항상 2바퀴로
 * 끝난다(우리 코퍼스 3,562개 파일 전부 1~2바퀴).
 *
 * 소진되면 null이다 — 그리고 **사용자는 그 사실을 듣지 못한다**: 상한 초과도,
 * 정제 후 공백도, 소진도 전부 같은 null이고, 화면은 조용히 파생 골격으로
 * 물러난다(라벨은 정직하지만, 붙여넣기가 거부됐다는 말은 어디에도 없다).
 * 이는 상한 초과에서 이미 존재하던 동작이라 새 결함은 아니지만, "fail closed"
 * 라는 말이 사용자에게도 보인다는 뜻은 아니라는 것을 여기 적어 둔다.
 */
const MAX_SANITIZE_ROUNDS = 12

export function sanitizeAsIsMarkup(
  raw: string,
): { markup: string; removed: number } | null {
  if (typeof raw !== 'string') return null
  if (new TextEncoder().encode(raw).length > AS_IS_MARKUP_MAX_BYTES) return null

  let markup = raw
  let removed = 0
  let reachedFixpoint = false
  for (let round = 0; round < MAX_SANITIZE_ROUNDS; round += 1) {
    const pass = sanitizePass(markup)
    removed += pass.removed
    if (pass.markup === markup) {
      reachedFixpoint = true
      break
    }
    markup = pass.markup
  }
  if (!reachedFixpoint) return null

  return markup.trim().length === 0 ? null : { markup, removed }
}

// 샌드박스는 스크립트를 막지만 <img>/<link>는 여전히 요청을 보낸다.
// 폐쇄망에서 바깥으로 나가는 시도 자체를 없앤다 — 이미지·스타일은
// 인라인/데이터 URI만 허용. 원문 자체는 손대지 않는다(진실 보존).
const AS_IS_FRAME_CSP =
  `<meta http-equiv="Content-Security-Policy" ` +
  `content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; font-src data:">`

/**
 * 프레임 안에서 링크가 향할 기본 목적지. 새 브라우징 컨텍스트를 요구하므로
 * `allow-popups` 없는 샌드박스가 그대로 삼킨다 — 즉 아무 일도 일어나지 않는다.
 */
const AS_IS_FRAME_BASE = '<base target="_blank">'

/** `target` 속성을 개명해 둘 무해한 이름. */
const NEUTRALIZED_TARGET_ATTR = 'data-as-is-inert-nav'

/**
 * `target` 속성의 **이름만** 바꿔 항해를 무력화한다. 값도, 앞뒤 바이트도
 * 건드리지 않는다.
 *
 * 라운드 5 — 이 자리에 있던 `/([\s/])target(\s*=)/gi`는 구분자를 요구했고,
 * 토크나이저는 요구하지 않는다: `<a href="x"target="_self">`는 구분자 바이트
 * 없이도 살아 있는 target이다. 그래서 문자 클래스가 아니라 스캐너로 찾는다
 * (근거는 scanTagAttributes 주석).
 */
function neutralizeNavigationTargets(markup: string): string {
  const targets = scanTagAttributes(markup).filter(
    (attr) => attr.name === 'target',
  )
  if (targets.length === 0) return markup
  let out = ''
  let cursor = 0
  for (const attr of targets) {
    out += markup.slice(cursor, attr.start) + NEUTRALIZED_TARGET_ATTR
    cursor = attr.nameEnd
  }
  return out + markup.slice(cursor)
}

/**
 * 저장된 원본 바이트를 담아 프레임에 넘길 문서.
 *
 * FINAL-REVIEW Critical — **프레임이 스스로 항해할 수 있었다.**
 * `sandbox=""`는 스크립트·폼·`meta refresh`·최상위 항해를 막지만, 샌드박스된
 * 문서가 **자기 자신을 이동시키는 것**은 언제나 허용된다. srcdoc의 CSP도
 * 항해를 규율하지 않는다(`navigate-to`는 출하된 적이 없고, 부모에는
 * `frame-src`가 없다). 붙여넣은 은행 화면은 링크 투성이다:
 *
 *   • **상대** href는 srcdoc이 부모의 base URL을 물려받으므로 **Studio 자신의
 *     오리진**으로 해석된다 → 클릭 한 번에 사용자의 바이트가 Studio의 SPA
 *     응답으로 바뀐다. 그러는 동안 열한 개의 표면이 전부 여전히 "원본"이라고
 *     말한다.
 *   • **절대** href는 어플라이언스 밖으로 나간다 — CSP 위반도, 샌드박스 위반도
 *     없이. 폐쇄망에서 이건 CSP 작업이 막으려던 바로 그 유출 경로다.
 *
 * 두 가지를 함께 건다. 여기서 항해가 **불가능**해야 하므로 하나로는 부족하다:
 *
 *   1. `<base target="_blank">` — target이 없거나 빈 링크(대다수)를 새 컨텍스트로
 *      돌린다. 샌드박스에 `allow-popups`가 없으므로 아무 일도 일어나지 않는다.
 *      우리 `<base>`가 문서에서 **처음**이므로("첫 base 요소가 이긴다") 붙여넣은
 *      마크업 안의 `<base target="_self">`가 이를 뒤집을 수 없다.
 *   2. `target` 속성 이름 개명 — 명시적 `target="_self"`는 base보다 우선하므로
 *      1번만으로는 그대로 새어나간다. 이름만 바꾸고 **값도, 주변 바이트도**
 *      건드리지 않는다.
 *
 * 바이트 약속과의 관계(명시): 저장물 `sourceMarkup.markup`은 **한 바이트도**
 * 바뀌지 않는다. 프레임에 들어가는 문서에서만 (a) head에 `<base>` 한 줄이
 * 추가되고 (b) `target` 속성의 **이름**이 바뀐다. `target`은 렌더에 아무
 * 영향이 없는 속성이라 화면은 동일하고, href·본문·원격 `<img>`·`${...}`
 * 자리표시자는 전부 그대로다. 대신 잃는 것도 정확히 말해 둔다: 프레임 안의
 * 링크는 이제 아무 데도 가지 않으며, 문서 내 앵커(`href="#top"`)도 마찬가지다.
 * 원본을 *보여주는* 화면에서 원본을 *떠나는* 동작을 없앤 것이다.
 *
 * 삭제가 아니라 **개명**인 이유는 I1과 같은 뿌리다: 삭제는 떨어져 있던
 * 두 조각을 붙여 새 구조를 만들 수 있다(`tar` + `/target="_self"` + `get=` →
 * ` target=`). 개명은 구분자를 그대로 두므로 무엇도 인접해지지 않고, 따라서
 * 한 번의 패스가 곧 고정점이다.
 */
export function buildAsIsFrameDocument(markup: string): string {
  const inert = neutralizeNavigationTargets(markup)
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `${AS_IS_FRAME_CSP}${AS_IS_FRAME_BASE}</head><body>${inert}</body></html>`
  )
}
